import { action, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { Id } from '@/infra/id'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import type { PromptScopeWorkOptions } from '@/features/jobs/scoping/components/OCRConfirmArchiveDialog/OCRConfirmArchiveDialogViewModel'

/**
 * Dialog to confirm and move an OCR scope work to ready to submit.
 */
export class OCRConfirmReviewDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  private _scopeWorkId: Id = Id.none

  constructor(
    private readonly _ocrScopeWorkStore: OCRScopeWorkStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Prompts the user to move the scope work to ready to submit.
   * @returns Promise resolving to true if moved, false if canceled
   */
  async prompt({ scopeWorkId, signal }: PromptScopeWorkOptions) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._scopeWorkId = scopeWorkId
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  @action.bound
  cancel() {
    this.modal.close()
  }

  /**
   * Closes the dialog right away; the request keeps the scope work's actions
   * pending until it settles.
   */
  review = task.resolved(async () => {
    this._prompt.respond(true)
    this.modal.close()

    await this._ocrScopeWorkStore
      .review(this._scopeWorkId)
      .catch(
        this._createAlertErrorHandler(
          'Moving the job to ready to submit failed',
        ),
      )
    this._newAlert()
      .saying('Success!', 'Job was moved to ready to submit.')
      .withState('success')
      .autoDismiss()
      .show()
  })
}
